import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCreateGroup, useUpdateGroup } from "~/api/queries";
import type { Group } from "~/api/types";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";
import { Input } from "~/foundation/components/Input";

/** Create and rename, one form, two modes — clean-room (§A.6.2). */
export function GroupDialog({ group, onClose }: { group?: Group; onClose: () => void }) {
  const { t } = useTranslation();
  const createGroup = useCreateGroup();
  const updateGroup = useUpdateGroup();
  const [name, setName] = useState(group?.name ?? "");
  const nameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const busy = createGroup.isPending || updateGroup.isPending;
  const isEdit = Boolean(group);

  const submit = () => {
    if (!name.trim()) {
      return;
    }
    if (isEdit && group) {
      updateGroup.mutate(
        { id: group.id, name: name.trim() },
        {
          onSuccess: () => {
            onClose();
            toast.success(t("Team renamed"));
          },
        },
      );
    } else {
      createGroup.mutate(
        { name: name.trim() },
        {
          onSuccess: (created) => {
            onClose();
            toast.success(t("Created “{{title}}”", { title: created.name }));
          },
        },
      );
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={isEdit ? t("Rename team") : t("New team")}
      closeLabel={t("Close")}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button variant="primary" disabled={!name.trim() || busy} onClick={submit}>
            {isEdit ? t("Save") : t("Create")}
          </Button>
        </>
      }
    >
      <Input
        ref={nameRef}
        label={t("Name")}
        fullWidth
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
    </Dialog>
  );
}
